import { useCallback, useEffect, useMemo, useState } from "react"

import ShippingOrder from "models/ShippingOrder"
import toastMessages from "stores/toastMessages"
import Reservation from "models/Reservation"
import { SortDirection } from "config/constants"
import { useTenantSettings } from "stores/tenantSettings"
import { IShippingOrderAssetsSort, IShippingResultAssetsSort, ShippingResultAssetsSortBy } from "../config"
import useShippingOrderAssetsExtended from "./useShippingOrderAssetsExtended"

interface IUseShippingOrderModalParams {
    shippingOrder: ShippingOrder
    reservation: Reservation
}

export default function useShippingOrderModal({
    shippingOrder,
    reservation,
}: IUseShippingOrderModalParams) {
    const { deliveryTerms, fetchDeliveryTerms, tenantWarehouses, fetchTenantWarehouses } = useTenantSettings()

    const initDeliveryTerms = useCallback(async () => {
        try {
            await fetchDeliveryTerms()
        } catch (e) {
            toastMessages.showSystemError()
        }
    }, [fetchDeliveryTerms])

    const initTenantWarehouses = useCallback(async () => {
        try {
            await fetchTenantWarehouses()
        } catch (e) {
            toastMessages.showSystemError()
        }
    }, [fetchTenantWarehouses])

    useEffect(() => {
        initDeliveryTerms()
    }, [initDeliveryTerms])

    useEffect(() => {
        initTenantWarehouses()
    }, [initTenantWarehouses])

    const deliveryTerm = deliveryTerms && shippingOrder.deliveryTermId ? deliveryTerms.entities.find(dt => dt.termId === shippingOrder.deliveryTermId) : undefined
    const responsibleWarehouse = tenantWarehouses && shippingOrder.responsibleWarehouseId ? tenantWarehouses.entities.find(w => w.id === shippingOrder.responsibleWarehouseId) : undefined

    const shippingOrderAssetIds = useMemo(() => {
        if (shippingOrder.assets.length === 0) return []
        return shippingOrder.assets.map((asset) => asset.assetId)
    }, [shippingOrder.assets])

    const [shippingOrderAssetsSort, setShippingOrderAssetsSort] = useState<IShippingOrderAssetsSort | null>(null)

    const [shippingResultAssetsSort, setShippingResultAssetsSort] = useState<IShippingResultAssetsSort>({
        sortBy: ShippingResultAssetsSortBy.operation,
        sortDirection: SortDirection.asc,
    })

    const reservationAssets = useMemo(() => reservation?.assets || [], [reservation])

    const {
        shippingOrderAssetsExtended,
        isLoadingAssetDisplayStatuses,
    } = useShippingOrderAssetsExtended({
        reservationStartDate: reservation?.time.reservationStart,
        reservationEndDate: reservation?.time.reservationEnd,
        reservationAssets,
        assets: shippingOrder.assets,
        locations: shippingOrder.locations,
        sort: shippingOrderAssetsSort,
    })

    return {
        deliveryTerm,
        responsibleWarehouse,
        shippingOrderAssetIds,
        shippingOrderAssetsSort,
        setShippingOrderAssetsSort,
        shippingResultAssetsSort,
        setShippingResultAssetsSort,
        shippingOrderAssetsExtended,
        isLoadingAssetDisplayStatuses,
    }
}
